/**
 * Release Deployment Modal
 *
 * Modal for executing release deployment automation with production/non-production
 * and dry-run options.
 */
import { useState } from 'react';

import { AlertCircle, Rocket } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';

import { useExecuteReleaseDeploymentMutation } from '@/store/api/releaseDeploymentApi';

interface ReleaseDeploymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ReleaseDeploymentModal = ({ open, onOpenChange }: ReleaseDeploymentModalProps) => {
  const [isProduction, setIsProduction] = useState<boolean>(false);
  const [dryRun, setDryRun] = useState<boolean>(true);
  const [executionResult, setExecutionResult] = useState<{
    success: boolean;
    message: string;
    output: string | null;
  } | null>(null);

  const [executeReleaseDeployment, { isLoading }] = useExecuteReleaseDeploymentMutation();

  const handleExecute = async () => {
    setExecutionResult(null);

    try {
      const result = await executeReleaseDeployment({
        isProduction,
        dryRun,
      }).unwrap();

      setExecutionResult(result);
    } catch (error: any) {
      // Handle error - could be a string or validation error array
      let errorMessage = 'Failed to execute release deployment';

      if (typeof error?.data?.detail === 'string') {
        errorMessage = error.data.detail;
      } else if (Array.isArray(error?.data?.detail)) {
        // Validation errors - extract the first message
        errorMessage = error.data.detail[0]?.msg || errorMessage;
      }

      setExecutionResult({
        success: false,
        message: errorMessage,
        output: null,
      });
    }
  };

  const handleClose = () => {
    setIsProduction(false);
    setDryRun(true);
    setExecutionResult(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Rocket className='h-5 w-5' />
            Release Deployment
          </DialogTitle>
          <DialogDescription>
            Execute release deployment automation using the sl-release-redeploy command. This
            requires can_deploy_production permission.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6 py-4'>
          {/* Environment Selection */}
          <div className='space-y-3'>
            <Label className='text-base font-semibold'>Environment</Label>
            <RadioGroup
              value={isProduction ? 'production' : 'non-production'}
              onValueChange={(value) => setIsProduction(value === 'production')}
              disabled={isLoading}
            >
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='non-production' id='non-production' />
                <Label htmlFor='non-production' className='cursor-pointer'>
                  Non-Production
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='production' id='production' />
                <Label htmlFor='production' className='cursor-pointer'>
                  Production
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Dry Run Checkbox */}
          <div className='flex items-center space-x-2'>
            <Checkbox
              id='dry-run'
              checked={dryRun}
              onCheckedChange={(checked) => setDryRun(checked === true)}
              disabled={isLoading}
            />
            <Label
              htmlFor='dry-run'
              className='cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
            >
              Dry Run (test mode - shows what would happen without executing)
            </Label>
          </div>

          {/* Production Warning */}
          {isProduction && !dryRun && (
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>
                <strong>Warning:</strong> You are about to execute a <strong>PRODUCTION</strong>{' '}
                release deployment. This will make changes to the production environment.
              </AlertDescription>
            </Alert>
          )}

          {/* Execution Results */}
          {executionResult && (
            <div className='space-y-2'>
              <Label className='text-base font-semibold'>Results</Label>
              <Alert variant={executionResult.success ? 'default' : 'destructive'}>
                <AlertDescription>{executionResult.message}</AlertDescription>
              </Alert>

              {executionResult.output && (
                <div className='space-y-2'>
                  <Label className='text-sm'>Command Output</Label>
                  <ScrollArea className='h-64 w-full rounded-md border bg-slate-950 p-4'>
                    <pre className='text-xs text-slate-50 font-mono whitespace-pre-wrap'>
                      {executionResult.output}
                    </pre>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isLoading}>
            {executionResult ? 'Close' : 'Cancel'}
          </Button>
          {!executionResult && (
            <Button onClick={handleExecute} disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className='animate-spin mr-2'>⏳</span>
                  Executing...
                </>
              ) : (
                <>Execute{dryRun ? ' Dry Run' : ''}</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
